/**
 * PATH: packages/ui/src/kanban/card-surface/registry.ts
 * FILE_ID: UI-KANBAN-CARDSURFACE-REGISTRY-001
 * PURPOSE: Enumerate every approved live Card projection and control policy.
 * WHY: A view cannot silently omit a capability if its policy is total and tested.
 * FLOW: Surface id → projection/mobile/control definition → adapter and generated tests.
 * DEPENDENCIES: contract.ts.
 * RELATED: ../components/board-view/boardAltViews.tsx; ../components/lane/LaneCardItem.tsx.
 */
import {
  CARD_COMMAND_IDS,
  CARD_SURFACE_IDS,
  type CardCommandId,
  type CardMobileTier,
  type CardSurfaceId,
  type CardSurfaceProjection,
} from './contract'

export type CardControlExposure = 'direct' | 'menu' | 'drawer' | 'unsupported'

export interface CardControlPolicy {
  desktop: CardControlExposure
  touch: CardControlExposure
  keyboard: CardControlExposure
}

export type CardControlManifest = Readonly<Record<CardCommandId, CardControlPolicy>>

export interface CardSurfaceDefinition {
  id: CardSurfaceId
  projection: CardSurfaceProjection
  mobileTier: CardMobileTier
  controls: CardControlManifest
  sourcePaths: readonly string[]
  semanticException?: string
}

const UNSUPPORTED_CONTROL: CardControlPolicy = {
  desktop: 'unsupported',
  touch: 'unsupported',
  keyboard: 'unsupported',
}

function controls(
  overrides: Partial<Record<CardCommandId, Partial<CardControlPolicy>>>,
): CardControlManifest {
  const manifest = {} as Record<CardCommandId, CardControlPolicy>
  for (const id of CARD_COMMAND_IDS) {
    manifest[id] = { ...UNSUPPORTED_CONTROL, ...overrides[id] }
  }
  return manifest
}

const TILE_CONTROLS = controls({
  open: { desktop: 'direct', touch: 'direct', keyboard: 'direct' },
  'open-context-menu': { desktop: 'menu', touch: 'menu', keyboard: 'menu' },
  'toggle-complete': { desktop: 'direct', touch: 'menu', keyboard: 'menu' },
  rename: { desktop: 'direct', touch: 'drawer', keyboard: 'direct' },
  'set-priority': { desktop: 'direct', touch: 'menu', keyboard: 'menu' },
  'set-action-date': { desktop: 'direct', touch: 'menu', keyboard: 'menu' },
  'set-due-date': { desktop: 'direct', touch: 'menu', keyboard: 'menu' },
  'set-followup-date': { desktop: 'menu', touch: 'menu', keyboard: 'menu' },
  assign: { desktop: 'direct', touch: 'menu', keyboard: 'menu' },
  move: { desktop: 'direct', touch: 'menu', keyboard: 'menu' },
  'add-placement': { desktop: 'menu', touch: 'menu', keyboard: 'menu' },
  'remove-placement': { desktop: 'menu', touch: 'menu', keyboard: 'menu' },
  archive: { desktop: 'direct', touch: 'menu', keyboard: 'menu' },
  restore: { desktop: 'menu', touch: 'menu', keyboard: 'menu' },
  delete: { desktop: 'menu', touch: 'menu', keyboard: 'menu' },
  pin: { desktop: 'menu', touch: 'menu', keyboard: 'menu' },
  unpin: { desktop: 'menu', touch: 'menu', keyboard: 'menu' },
  'toggle-checklist': { desktop: 'direct', touch: 'menu', keyboard: 'menu' },
  'quick-capture': { desktop: 'direct', touch: 'drawer', keyboard: 'direct' },
  'open-email': { desktop: 'direct', touch: 'drawer', keyboard: 'menu' },
  'allocate-email': { desktop: 'direct', touch: 'menu', keyboard: 'menu' },
  'compose-email': { desktop: 'direct', touch: 'menu', keyboard: 'menu' },
  'open-workbook': { desktop: 'direct', touch: 'menu', keyboard: 'menu' },
  'open-flowchart': { desktop: 'direct', touch: 'menu', keyboard: 'menu' },
  'open-entries': { desktop: 'direct', touch: 'drawer', keyboard: 'menu' },
  'open-thread': { desktop: 'direct', touch: 'direct', keyboard: 'direct' },
  'create-comment': { desktop: 'drawer', touch: 'drawer', keyboard: 'drawer' },
  nest: { desktop: 'direct', touch: 'menu', keyboard: 'menu' },
  'promote-to-top-level': { desktop: 'menu', touch: 'menu', keyboard: 'menu' },
  'copy-to-board': { desktop: 'menu', touch: 'menu', keyboard: 'menu' },
  duplicate: { desktop: 'menu', touch: 'menu', keyboard: 'menu' },
  snooze: { desktop: 'menu', touch: 'menu', keyboard: 'menu' },
  block: { desktop: 'menu', touch: 'menu', keyboard: 'menu' },
  'schedule-meeting': { desktop: 'menu', touch: 'menu', keyboard: 'menu' },
  'convert-to-notebook': { desktop: 'menu', touch: 'menu', keyboard: 'menu' },
  'revert-last-action': { desktop: 'menu', touch: 'menu', keyboard: 'menu' },
})

const SUMMARY_CONTROLS = controls({
  open: { desktop: 'direct', touch: 'direct', keyboard: 'direct' },
  'open-context-menu': { desktop: 'menu', touch: 'menu', keyboard: 'menu' },
})

const TIMELINE_CONTROLS = controls({
  open: { desktop: 'direct', touch: 'direct', keyboard: 'direct' },
  'open-context-menu': { desktop: 'menu', touch: 'menu', keyboard: 'menu' },
  'set-action-date': { desktop: 'direct', touch: 'drawer', keyboard: 'menu' },
  'set-due-date': { desktop: 'direct', touch: 'drawer', keyboard: 'menu' },
  assign: { desktop: 'direct', touch: 'drawer', keyboard: 'menu' },
})

function surface(
  id: CardSurfaceId,
  projection: CardSurfaceProjection,
  mobileTier: CardMobileTier,
  sourcePaths: readonly string[],
  controlsForSurface: CardControlManifest,
  semanticException?: string,
): CardSurfaceDefinition {
  return {
    id,
    projection,
    mobileTier,
    sourcePaths,
    controls: controlsForSurface,
    semanticException,
  }
}

export const CARD_SURFACE_REGISTRY = {
  'board-lane-card': surface(
    'board-lane-card',
    'tile',
    'full',
    ['components/board-view/BoardLaneColumn.tsx', 'components/lane/LaneCardItem.tsx'],
    TILE_CONTROLS,
  ),
  'ar-ap-lane-card': surface(
    'ar-ap-lane-card',
    'tile',
    'full',
    ['components/board-view/BoardFlatCanvas.tsx'],
    TILE_CONTROLS,
  ),
  'time-bucket-card': surface(
    'time-bucket-card',
    'tile',
    'full',
    ['components/BoardGanttBucketView.tsx'],
    TILE_CONTROLS,
    'Adds Time anchor and day-hours decorations without changing card capabilities.',
  ),
  'smart-time-card': surface(
    'smart-time-card',
    'tile',
    'full',
    ['components/BoardSmartTimeView.tsx'],
    TILE_CONTROLS,
    'Adds action-first schedule anchor decoration.',
  ),
  'assignee-swimlane-card': surface(
    'assignee-swimlane-card',
    'tile',
    'full',
    ['components/BoardSwimlaneView.tsx'],
    TILE_CONTROLS,
    'Assignment drops are a view-specific command input, not a different card runtime.',
  ),
  'split-lane-card': surface(
    'split-lane-card',
    'tile',
    'desktop-redirect',
    ['components/board-view/BoardSwimlaneSplitView.tsx'],
    TILE_CONTROLS,
    'Phone currently falls back to Flat Board until touch split behavior is mechanically proven.',
  ),
  'checklist-row': surface(
    'checklist-row',
    'checklist-row',
    'full',
    ['components/KanbanChecklistRow.tsx'],
    TILE_CONTROLS,
    'Dense row retains the same runtime while showing a reduced direct-control set.',
  ),
  'table-row': surface(
    'table-row',
    'table-row',
    'review',
    ['components/board-table/BoardTableRowTr.tsx'],
    SUMMARY_CONTROLS,
  ),
  'calendar-event': surface(
    'calendar-event',
    'calendar-event',
    'review',
    ['components/BoardCalendarView.tsx'],
    TIMELINE_CONTROLS,
  ),
  'calendar-unscheduled-card': surface(
    'calendar-unscheduled-card',
    'calendar-event',
    'review',
    ['components/BoardCalendarView.tsx'],
    TIMELINE_CONTROLS,
  ),
  'timeline-bar': surface(
    'timeline-bar',
    'timeline-bar',
    'review',
    ['components/gantt-timeline/GanttBar.tsx'],
    TIMELINE_CONTROLS,
  ),
  'timeline-unscheduled-card': surface(
    'timeline-unscheduled-card',
    'timeline-bar',
    'review',
    ['components/gantt-timeline/UnscheduledTray.tsx'],
    TIMELINE_CONTROLS,
  ),
  'planner-timeline-bar': surface(
    'planner-timeline-bar',
    'timeline-bar',
    'review',
    ['packages/kanban-host/src/PlannerPage.tsx'],
    TIMELINE_CONTROLS,
    'Uses source-board identity rather than synthetic display lane IDs for mutations.',
  ),
  'planner-reminder': surface(
    'planner-reminder',
    'agenda-row',
    'full',
    ['components/RemindersList.tsx'],
    SUMMARY_CONTROLS,
  ),
  'cashflow-row': surface(
    'cashflow-row',
    'financial-row',
    'review',
    ['components/BoardCashFlowView.tsx'],
    SUMMARY_CONTROLS,
  ),
  'cashflow-dag-node': surface(
    'cashflow-dag-node',
    'financial-node',
    'review',
    ['components/BoardCashFlowDag.tsx'],
    SUMMARY_CONTROLS,
  ),
  'my-cards-row': surface(
    'my-cards-row',
    'agenda-row',
    'full',
    ['components/KanbanInboxRail.tsx'],
    SUMMARY_CONTROLS,
    'Open carries the source board so drawer data and invalidation remain scoped correctly.',
  ),
  'notebook-source-reference': surface(
    'notebook-source-reference',
    'reference',
    'review',
    ['notebook/NotebookSourceCardsRail.tsx'],
    SUMMARY_CONTROLS,
    'Read-only provenance surface; destructive commands remain unavailable.',
  ),
  'calendar-action': surface(
    'calendar-action',
    'multi-action',
    'review',
    ['../calendar/actions-board/ActionCard.tsx'],
    SUMMARY_CONTROLS,
    'One Card may emit several dated action projections.',
  ),
} as const satisfies Record<CardSurfaceId, CardSurfaceDefinition>

export const CARD_SURFACE_EXEMPTIONS = {
  'cost-dashboard': 'Aggregate-only view; it renders no individual Card.',
  'global-search-result': 'Navigation-only search reference.',
  'allocation-card-selector': 'Transactional selector; it links an email to a chosen Card.',
  'detail-recovery-row': 'Scoped drawer/recovery reference with deliberately restricted commands.',
} as const

export function registryCoverageIssues(): string[] {
  const declared = new Set(Object.keys(CARD_SURFACE_REGISTRY))
  return CARD_SURFACE_IDS
    .filter((surfaceId) => !declared.has(surfaceId))
    .map((surfaceId) => `Missing registry entry: ${surfaceId}`)
}
